import { useEffect, useRef, useState } from "react";

import { ExperimentFlags } from "@shared/utils.experimentation";
import {
  useBasketBuckets,
  useBasketTotals,
  useCurrencySymbol,
  useFeatureFlag,
  useLimitless
} from "hooks";
import { useScreenViewport } from "lib/checkViewport";
import { useUser } from "lib/user";

import {
  membersSaveText,
  remainingSpendingLimitText,
  savingsTodayText,
  thisOrderSavingsText
} from "./utils";

export const useBannerContent = (
  variant?: string
): { bannerContent: string[]; showMemberBenefitsLink: boolean } => {
  const currencySymbol = useCurrencySymbol();
  const user = useUser();
  const { memberSavings, guestSavings } = useBasketTotals();
  const savingToUse = user.isMember ? memberSavings : guestSavings;
  const { hasLimitlessEnabled } = useLimitless();
  const [showRemainingLimit, setShowRemainingLimit] = useState(false);
  const [showMembersSaveText, setShowMembersSaveText] = useState(false);
  const [showMemberBenefitsLink, setShowMemberBenefitsLink] = useState(false);
  const [bannerContent, setBannerContent] = useState<string[]>([
    membersSaveText
  ]);
  const [currentSavingsText, setCurrentSavingsText] = useState<string>("");
  const { products, memberships, remainingLimit } = useBasketBuckets();
  const isMiniBasket = variant === "miniBasket";
  const hasBasketMembership =
    user.hasMembershipInBag || user.hasGiftMembershipInBag;
  const [loadingMemberBenefitsFeatureFlag, memberBenefitsFeatureFlag] =
    useFeatureFlag(ExperimentFlags.USE_MEMBER_BENEFITS_PAGE, "0");
  // Show remaining limit when is not limitless
  // Show remaining limit when no membership in bag and is member
  useEffect(() => {
    if (hasLimitlessEnabled) {
      setShowRemainingLimit(false);
    } else {
      setShowRemainingLimit(!hasBasketMembership);
    }
  }, [hasLimitlessEnabled, hasBasketMembership]);

  // Show "Members save up to 70%" when (membership & no products) in bag
  // Don't show when is already a member
  useEffect(() => {
    if (showRemainingLimit || user.isMember) {
      setShowMembersSaveText(false);
    } else {
      setShowMembersSaveText(!hasBasketMembership || savingToUse <= 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, showRemainingLimit, user.isMember]);

  // Set current savings text for this order (if membership in bag)
  // Set current savings text for today (for existing member)
  useEffect(() => {
    if (savingToUse > 0) {
      const orderSavingsText = thisOrderSavingsText(
        savingToUse,
        currencySymbol
      );
      const onSavingTodayText = savingsTodayText(savingToUse, currencySymbol);

      setCurrentSavingsText(
        products.length && memberships.length
          ? orderSavingsText
          : onSavingTodayText
      );
    } else {
      setCurrentSavingsText("");
    }
  }, [products, memberships, savingToUse, currencySymbol]);

  // Set the banner content
  useEffect(() => {
    const content = [
      currentSavingsText,
      showRemainingLimit &&
        remainingSpendingLimitText(remainingLimit, currencySymbol),
      showMembersSaveText && membersSaveText
    ].filter((text): text is string => !!text);

    setBannerContent(content.length ? content : [membersSaveText]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentSavingsText,
    remainingLimit,
    showRemainingLimit,
    showMembersSaveText
  ]);

  useEffect(() => {
    setShowMemberBenefitsLink(
      !isMiniBasket &&
        !loadingMemberBenefitsFeatureFlag &&
        memberBenefitsFeatureFlag === "1"
    );
  }, [
    isMiniBasket,
    user,
    loadingMemberBenefitsFeatureFlag,
    memberBenefitsFeatureFlag
  ]);

  return {
    bannerContent,
    showMemberBenefitsLink
  };
};

const delay = 5000;

type UseSlideReturnType = {
  setMouseDown: (val: boolean) => void;
};

export const useSlide = (itemLen: number): UseSlideReturnType => {
  const timerRef = useRef<number>(0);
  const [mouseDown, setMouseDown] = useState(false);
  const { isMobile } = useScreenViewport();

  useEffect(() => {
    const slide = () => {
      const container = document.getElementById("member-sticky-footer");
      for (let index = 0; index < itemLen; index += 1) {
        const elem = document.getElementById(`sticky-footer-item-${index}`);
        if (elem && container) {
          const { left } = elem.getBoundingClientRect();
          if (left === 0) {
            const nextElemIndex = index >= itemLen - 1 ? 0 : index + 1;

            const itemWidth = container.firstElementChild?.clientWidth || 0;
            const nextScroll = nextElemIndex * itemWidth;

            container.scrollTo({ left: nextScroll, behavior: "smooth" });
            break;
          }
        }
      }
      timerRef.current = window.setTimeout(slide, delay);
    };

    if (timerRef.current) window.clearTimeout(timerRef.current);
    // autoplay mode must be on when viewport is mobile and no user interaction
    if (!mouseDown && isMobile) {
      timerRef.current = window.setTimeout(slide, delay);
    }

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [itemLen, mouseDown, isMobile]);

  return {
    setMouseDown
  };
};
